import FormInput from '@form/FormInput';
import { Form } from '@form/index';
import { useApi } from '@hooks/useApi';
import { Blockquote } from '@ui';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useClubSlug } from '../../Clubs/hooks';
import { CompactAllocation } from '../types';
import RebookingSlotsFieldArray from './RebookingSlotsFieldArray';

interface Props {
    allocation: CompactAllocation;
    callback?: (data: { link: string; token: string }) => any;
}

interface RebookingSlotFormValue {
    date?: string | null;
    start_time?: string;
    end_time?: string;
}

type PricingMode = 'dynamic' | 'fixed_price' | 'percentage_discount' | 'fixed_discount';

// Converts a form value to a number, returning null for empty/invalid inputs
const normalizeNumber = (value: unknown) => {
    if (value === '' || value === null || value === undefined) return null;

    const number = Number(value);
    return Number.isNaN(number) ? null : number;
};

// Renders pricing mode selector and the corresponding price/discount input field
const PricingConfigurationFields = () => {
    const { watch, setValue } = useFormContext();
    const { t } = useTranslation('space-manager');
    const pricingMode = watch('pricing_mode') as PricingMode;

    const pricingModeOptions = [
        { key: 'dynamic', label: t('allocationList.rebookingLink.pricingModeOptions.dynamic') },
        { key: 'fixed_price', label: t('allocationList.rebookingLink.pricingModeOptions.fixedPrice') },
        { key: 'percentage_discount', label: t('allocationList.rebookingLink.pricingModeOptions.percentageDiscount') },
        { key: 'fixed_discount', label: t('allocationList.rebookingLink.pricingModeOptions.fixedDiscount') }
    ];

    useEffect(() => {
        if (pricingMode !== 'fixed_price') setValue('price', '');
        if (pricingMode !== 'percentage_discount') setValue('discount_percentage', '');
        if (pricingMode !== 'fixed_discount') setValue('discount_fixed', '');
    }, [pricingMode, setValue]);

    return (
        <>
            <FormInput.Select
                name="pricing_mode"
                label={t('allocationList.rebookingLink.pricingModeLabel')}
                options={pricingModeOptions}
                description={t('allocationList.rebookingLink.pricingModeDescription')}
            />

            {pricingMode === 'fixed_price' && (
                <FormInput
                    type="number"
                    min={0}
                    step={0.01}
                    name="price"
                    label={t('allocationList.rebookingLink.fixedPriceLabel')}
                    labelRight="€"
                    helperText={t('allocationList.rebookingLink.fixedPriceHelper')}
                />
            )}

            {pricingMode === 'percentage_discount' && (
                <FormInput
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    name="discount_percentage"
                    label={t('allocationList.rebookingLink.pricingModeOptions.percentageDiscount')}
                    labelRight="%"
                    helperText={t('allocationList.rebookingLink.percentageDiscountHelper')}
                />
            )}

            {pricingMode === 'fixed_discount' && (
                <FormInput
                    type="number"
                    min={0}
                    step={0.01}
                    name="discount_fixed"
                    label={t('allocationList.rebookingLink.pricingModeOptions.fixedDiscount')}
                    labelRight="€"
                    helperText={t('allocationList.rebookingLink.fixedDiscountHelper')}
                />
            )}
        </>
    );
};

// Renders recipient selector and context-appropriate info text
const RecipientField = () => {
    const { watch } = useFormContext();
    const { t } = useTranslation('space-manager');
    const recipient = watch('recipient');

    const recipientOptions = [
        { key: 'main_booker', label: t('allocationList.rebookingLink.recipientOptions.mainBooker') },
        { key: 'all_bookers', label: t('allocationList.rebookingLink.recipientOptions.allBookers') }
    ];

    return (
        <>
            <FormInput.Select
                name="recipient"
                label={t('allocationList.rebookingLink.recipientLabel')}
                options={recipientOptions}
                description={t('allocationList.rebookingLink.recipientDescription')}
            />
            {recipient === 'all_bookers' ? (
                <Blockquote color="warning" className="tw-mb-4">
                    {t('allocationList.rebookingLink.recipientAllBookersWarning')}
                </Blockquote>
            ) : (
                <Blockquote color="warning" className="tw-mb-4">
                    {t('allocationList.rebookingLink.recipientMainBookerWarning')}
                </Blockquote>
            )}
        </>
    );
};

// Renders the full rebooking link creation form with pricing, recipient and slot options
const AllocationRebookingLinkForm: FC<Props> = ({ allocation, callback }) => {
    const api = useApi();
    const clubSlug = useClubSlug();
    const { t } = useTranslation('space-manager');
    const [isLoading, setIsLoading] = useState(false);

    // Builds and POSTs the rebooking link payload from form values
    const handleSubmit = (data: FieldValues) => {
        setIsLoading(true);

        const expiresAt = data.expires_at_date
            ? moment(`${data.expires_at_date} ${data.expires_at_time || '23:59'}`, 'YYYY-MM-DD HH:mm').toISOString()
            : null;
        const slots = ((data.slots ?? []) as RebookingSlotFormValue[])
            .filter((slot) => slot?.date && slot?.start_time && slot?.end_time)
            .map((slot) => ({
                start: moment(`${slot.date} ${slot.start_time}`, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss'),
                end: moment(`${slot.date} ${slot.end_time}`, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss')
            }));

        return api({
            url: `club/${clubSlug}/space-manager/allocation/${allocation.id}/rebooking-link`,
            method: 'POST',
            data: {
                is_free: !!data.is_free,
                price: data.pricing_mode === 'fixed_price' ? normalizeNumber(data.price) : null,
                discount_percentage:
                    data.pricing_mode === 'percentage_discount' ? normalizeNumber(data.discount_percentage) : null,
                discount_fixed: data.pricing_mode === 'fixed_discount' ? normalizeNumber(data.discount_fixed) : null,
                expires_at: expiresAt,
                slots,
                send_to_all_bookers: data.recipient === 'all_bookers',
                apply_rules: data.apply_rules ?? true
            },
            onBefore: () => setIsLoading(false),
            onSuccess: (responseData) => callback?.(responseData)
        });
    };

    return (
        <Form
            onSubmit={handleSubmit}
            defaultValues={{
                is_free: false,
                pricing_mode: 'dynamic',
                price: '',
                discount_percentage: '',
                discount_fixed: '',
                expires_at_date: null,
                expires_at_time: '',
                slots: [],
                recipient: 'main_booker',
                apply_rules: true
            }}
        >
            <RecipientField />

            <Blockquote color="warning" className="tw-mb-4">
                {t('allocationList.rebookingLink.priceInfoBlockquote')}
            </Blockquote>
            <PricingConfigurationFields />
            <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2">
                <FormInput.DatePicker
                    name="expires_at_date"
                    label={t('allocationList.rebookingLink.expiresAtDateLabel')}
                    helperText={t('allocationList.rebookingLink.expiresAtDateHelper')}
                />
                <FormInput
                    type="time"
                    name="expires_at_time"
                    label={t('allocationList.rebookingLink.expiresAtTimeLabel')}
                    helperText={t('allocationList.rebookingLink.expiresAtTimeHelper')}
                />
            </div>
            <FormInput.Checkbox
                name="apply_rules"
                label={t('allocationList.rebookingLink.applyRulesLabel')}
            />
            <Blockquote className="tw-mb-2 tw-mt-4">
                {t('allocationList.rebookingLink.slotsBlockquote')}
            </Blockquote>
            <RebookingSlotsFieldArray name="slots" />
            <FormInput.Submit isLoading={isLoading}>{t('allocationList.modals.createRebookingLink')}</FormInput.Submit>
        </Form>
    );
};

export default AllocationRebookingLinkForm;
